import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  Flex,
  Stack,
} from "@chakra-ui/react";
import YouTube from "react-youtube";
import { FaEllipsisV, FaPlus, FaSync, FaPrint } from "react-icons/fa";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../Globalapi/Globalapi";

const View_Material = () => {
  const [materials, setMaterials] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [videoId, setVideoId] = useState("");
  const [materialUrl, setMaterialUrl] = useState("");
  const [isYouTube, setIsYouTube] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    // Fetch classes
    axios
      .get(`${BASE_URL}/admin/class/class_master.php`)
      .then((response) => {
        console.log("Classes Response:", response.data);
        setClasses(response.data.data || []);
      })
      .catch((error) => {
        console.error("Error fetching classes:", error);
      });
  }, []);

  useEffect(() => {
    if (selectedClass) {
      // Fetch course materials based on the selected class
      axios
        .post(
          `${BASE_URL}/staff/course_materials/list_all_course_material.php`,
          {
            class_id: selectedClass,
          }
        )
        .then((response) => {
          console.log("Materials Response:", response.data);
          if (response.data.success) {
            setMaterials(response.data.materials || []);
            setTotalPages(
              Math.ceil((response.data.materials || []).length / rowsPerPage)
            );
          } else {
            console.error("Error from API:", response.data.message);
          }
        })
        .catch((error) => {
          console.error("Error fetching course materials:", error);
        });
    } else {
      setMaterials([]);
    }
  }, [selectedClass, rowsPerPage]);

  useEffect(() => {
    if (selectedClass) {
      setFilteredMaterials(
        materials.filter((material) => material.class_id === selectedClass)
      );
    } else {
      setFilteredMaterials(materials);
    }
  }, [selectedClass, materials]);

  const extractYouTubeID = (url) => {
    // Handle standard YouTube URLs
    const standardMatch = url.match(
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^\s&]+)/
    );
    if (standardMatch) return standardMatch[1];

    // Handle shortened YouTube URLs
    const shortenedMatch = url.match(
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^\s&]+)/
    );
    if (shortenedMatch) return shortenedMatch[1];

    // If no match found
    return null;
  };

  const handleVideoClick = (url) => {
    const videoID = extractYouTubeID(url);
    if (videoID) {
      setVideoId(videoID);
      setMaterialUrl(url);
      setIsYouTube(true);
      setIsOpen(true);
    } else {
      setVideoId("");
      setMaterialUrl(url);
      setIsYouTube(false);
      setIsOpen(true);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const closeModal = () => {
    setIsOpen(false);
    setVideoId("");
    setMaterialUrl("");
    setIsYouTube(false);
  };

  const opts = {
    height: "600",
    width: "100%",
  };

  return (
    <Box>
      <div style={{ backgroundColor: "#FBFBFD", height: "100vh" }}>
        <div className="teacher-list-container">
          <div className="header">
            <h1 style={{ fontSize: "20px", margin: 0 }}>Course Material</h1>
          </div>

          <div className="breadcrumb">
            <Link
              to="/admin"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <span>Dashboard</span>
            </Link>
            / <span>Course Material</span>
          </div>
        </div>
        <Box p={6} style={{ overflowX: "auto" }}>
          <Flex
            justify="space-between"
            align="center"
            className="sb"
            p={3}
            direction={{ base: "column", md: "row" }}
            gap={4} // Add spacing between rows in smaller views
          >
            {/* Rows Per Page Section */}
            <Flex
              align="center"
              width={{ base: "100%", md: "auto" }} // Full width on small screens
              justify={{ base: "space-between", md: "flex-start" }} // Space out items in smaller views
            >
              <Text mr={2} width="auto">
                Rows Per Page
              </Text>
              <Select
                value={rowsPerPage}
                width={{ base: "190px", md: "100px" }} // Full width on small screens
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
              </Select>
            </Flex>

            {/* Class Selection Section */}
            <FormControl id="class" width={{ base: "100%", md: "auto" }}>
              <Select
                placeholder="Select Class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                width="100%" // Ensure dropdown spans full width in smaller views
              >
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.class}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Flex>

          <Box overflowX="auto" maxWidth="100%">
            <Table
              sx={{
                "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              }}
            >
              <Thead>
                <Tr>
                  <Th>
                    <Box textAlign="center">File Name</Box>
                  </Th>
                  <Th>
                    <Box textAlign="center">Class</Box>
                  </Th>
                  <Th>
                    <Box textAlign="center">Subject</Box>
                  </Th>
                  <Th>
                    <Box textAlign="center">Caption</Box>
                  </Th>
                  <Th>
                    <Box textAlign="center">Staff</Box>
                  </Th>
                  <Th>
                    <Box textAlign="center">Material File</Box>
                  </Th>
                  <Th>
                    <Box textAlign="center">Material Link</Box>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredMaterials.length > 0 ? (
                  filteredMaterials.map((material) => (
                    <Tr key={material.id}>
                      <Td>
                        <Box textAlign="center">{material.file_name}</Box>{" "}
                      </Td>
                      <Td>
                        <Box textAlign="center">{material.class || "N/A"}</Box>{" "}
                      </Td>
                      <Td>
                        <Box textAlign="center">
                          {material.subject || "N/A"}
                        </Box>{" "}
                      </Td>
                      <Td>
                        <Box textAlign="center">
                          {material.caption || "N/A"}
                        </Box>{" "}
                      </Td>
                      <Td>
                        <Box textAlign="center">{material.name || "N/A"}</Box>{" "}
                      </Td>
                      <Td>
                        <Box textAlign="center">
                          {material.file_url ? (
                            <a
                              href={material.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View File
                            </a>
                          ) : (
                            "No File"
                          )}
                        </Box>
                      </Td>
                      <Td>
                        <Box textAlign="center">
                          {material.material_link ? (
                            <Button
                              onClick={() =>
                                handleVideoClick(material.material_link)
                              }
                            >
                              View Material
                            </Button>
                          ) : (
                            "No Link"
                          )}
                        </Box>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="7">
                      <Text textAlign="center">No materials available</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
          <Flex justify="end" borderTopWidth="1px" className="sb2" p={3}>
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>
            <Stack direction="row" mx={2}>
              {currentPage > 1 && (
                <>
                  <Button onClick={() => handlePageChange(1)}>1</Button>
                  {currentPage > 2 && <Text>...</Text>}
                </>
              )}
              <Button backgroundColor="teal" color="white">
                {currentPage}
              </Button>
              {currentPage < totalPages - 1 && (
                <>
                  <Text>...</Text>
                  <Button onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </Button>
                </>
              )}
            </Stack>
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Flex>
        </Box>
      </div>

      {/* Modal for video */}
      <Modal isOpen={isOpen} onClose={closeModal} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Material</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isYouTube ? (
              <YouTube videoId={videoId} opts={opts} />
            ) : (
              <Text>
                The content cannot be displayed in an iframe.
                <a
                  href={materialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "blue" }}
                >
                  Click here to view it directly
                </a>
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default View_Material;
